import React, { useEffect, useRef } from 'react';
import { CheckCircle2, Lock } from 'lucide-react';
function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const children = el.querySelectorAll('.reveal-card');
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('opacity-100', 'translate-y-0');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.15,
      rootMargin: '0px 0px -40px 0px'
    });
    children.forEach(child => observer.observe(child));
    return () => observer.disconnect();
  }, []);
  return ref;
}
const alternatives = [{
  label: 'Private energy healing session',
  cost: '$150 – $300'
}, {
  label: 'Sound bath group class (×7 days)',
  cost: '$315 – $560'
}, {
  label: 'Spiritual wellness retreat (weekend)',
  cost: '$500 – $2,000'
}, {
  label: 'Protection crystal kit (Etsy)',
  cost: '$35 – $120'
}, {
  label: 'Anxiety therapy session',
  cost: '$100 – $200'
}];
export function PriceAnchoringSection() {
  const containerRef = useScrollReveal();
  return <section id="tools-section" className="relative w-full py-14 px-4 bg-navy overflow-hidden" data-id="element-158">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gold/5 rounded-full blur-[120px] pointer-events-none" data-id="element-159"></div>

      <div ref={containerRef} className="relative z-10 max-w-3xl mx-auto flex flex-col items-center" data-id="element-160">
        {/* PART 1: Section Opener */}
        <p className="text-gold text-sm tracking-[0.2em] uppercase mb-6 font-medium text-center" data-id="element-161">
          EVERYTHING YOU RECEIVE TODAY
        </p>
        <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-cream text-center leading-tight mb-4" data-id="element-162">
          Here Is Every Tool You Need To Clear
          <br className="hidden md:block" data-id="element-163" />
          Your Frequency — And Never Fall Back
        </h2>
        <p className="text-rose text-lg md:text-xl text-center mb-16 max-w-xl font-serif italic" data-id="element-164">
          A complete system designed so your success is not left to chance — or
          faith alone.
        </p>

        {/* PART 2: Deliverables Stack */}
        <div className="w-full space-y-6 mb-16" data-id="element-165">
          {/* CARD 1 — Main Deliverable */}
          <div className="reveal-card opacity-0 translate-y-8 transition-all duration-700 ease-out bg-[#0F1E30] border-l-4 border-gold rounded-xl p-6 md:p-8 relative overflow-hidden" data-id="element-166">
            <div className="inline-block bg-gold/20 border border-gold/50 text-gold text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full mb-4" data-id="element-167">
              MAIN
            </div>
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4" data-id="element-168">
              <div className="flex-1" data-id="element-169">
                <div className="flex items-center gap-3 mb-2" data-id="element-170">
                  <span className="text-3xl" data-id="element-171">🎵</span>
                  <h3 className="font-serif text-2xl md:text-3xl text-cream" data-id="element-172">
                    The 417hz Lucky Flush Audio
                  </h3>
                </div>
                <p className="text-cream/50 text-sm mb-4" data-id="element-173">
                  7-Day Frequency Clearing Track — MP3 Format
                </p>
                <p className="text-cream/70 text-base leading-relaxed max-w-lg" data-id="element-174">
                  The core of the system. One audio track engineered at the
                  417hz Solfeggio Frequency — the frequency clinically
                  associated with dissolving energetic resistance and
                  facilitating change. Listen once daily for 7 consecutive days.
                  No rituals. No reading. No effort beyond pressing play.
                </p>
              </div>
              <div className="flex flex-col items-end flex-shrink-0 md:pt-8" data-id="element-175">
                <span className="price-strike text-xl line-through" data-id="element-176">$54</span>
                <span className="text-gold font-semibold text-sm mt-1" data-id="element-177">
                  Included
                </span>
              </div>
            </div>
          </div>

          {/* CARD 2 — Companion Piece */}
          <div className="reveal-card opacity-0 translate-y-8 transition-all duration-700 ease-out delay-100 bg-[#0F1E30] border-l-4 border-gold rounded-xl p-6 md:p-8 relative overflow-hidden" data-id="element-178">
            <div className="inline-block bg-cream/10 border border-cream/20 text-cream/70 text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full mb-4" data-id="element-179">
              INCLUDED
            </div>
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4" data-id="element-180">
              <div className="flex-1" data-id="element-181">
                <div className="flex items-center gap-3 mb-2" data-id="element-182">
                  <span className="text-3xl" data-id="element-183">📄</span>
                  <h3 className="font-serif text-xl md:text-2xl text-cream" data-id="element-184">
                    Quick Start Listening Card
                  </h3>
                </div>
                <p className="text-cream/50 text-sm mb-4" data-id="element-185">
                  1-Page PDF — Your Complete Listening Guide
                </p>
                <p className="text-cream/70 text-base leading-relaxed max-w-lg" data-id="element-186">
                  Tells you exactly when to listen, for how long, in what
                  position, and what to focus on. Removes all guesswork from Day
                  1.
                </p>
              </div>
              <div className="flex flex-col items-end flex-shrink-0 md:pt-8" data-id="element-187">
                <span className="price-strike text-xl line-through" data-id="element-188">$17</span>
                <span className="text-gold font-semibold text-sm mt-1" data-id="element-189">
                  Included
                </span>
              </div>
            </div>
          </div>

          {/* CARD 3 — Bonus #1 (PREMIUM) */}
          <div className="reveal-card opacity-0 translate-y-8 transition-all duration-700 ease-out delay-200 shimmer-border !bg-[#0F1E30] border-l-4 !border-l-gold rounded-2xl p-8 md:p-10 relative overflow-hidden" data-id="element-190">
            <div className="ribbon-exclusive" data-id="element-191">EXCLUSIVE</div>

            <div className="inline-block bg-gold/20 border border-gold/50 text-gold text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full mb-5" data-id="element-192">
              BONUS #1
            </div>
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5" data-id="element-193">
              <div className="flex-1" data-id="element-194">
                <div className="flex items-center gap-3 mb-2" data-id="element-195">
                  <span className="text-4xl" data-id="element-196">👑</span>
                  <h3 className="font-serif text-2xl md:text-3xl text-cream" data-id="element-197">
                    Direct Access To Serena Voss
                  </h3>
                </div>
                <p className="text-gold italic text-base mb-5 font-serif" data-id="element-198">
                  Your Personal Frequency Guide — Available Throughout Your
                  Entire Journey
                </p>
                <div className="text-cream/70 text-base leading-relaxed max-w-lg space-y-3" data-id="element-199">
                  <p data-id="element-200">
                    Your direct line to the creator. Not a chatbot, not a
                    support inbox. Serena herself guides you from Day 1 through
                    Day 7 and beyond.
                  </p>
                  <p data-id="element-201">
                    Feeling uncertain? Need confirmation you're on the right
                    path?{' '}
                    <span className="text-cream font-medium" data-id="element-202">
                      She is there. Your guide. Your certainty.
                    </span>
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end flex-shrink-0 md:pt-10" data-id="element-203">
                <span className="price-strike text-2xl line-through" data-id="element-204">$197</span>
                <span className="text-gold font-bold text-3xl mt-2 animate-free-glow" data-id="element-205">
                  FREE
                </span>
              </div>
            </div>
          </div>

          {/* CARD 4 — Bonus #2 */}
          <div className="reveal-card opacity-0 translate-y-8 transition-all duration-700 ease-out delay-300 bg-[#0F1E30] border-l-4 border-gold rounded-xl p-6 md:p-8 relative overflow-hidden" data-id="element-206">
            <div className="inline-block bg-gold/20 border border-gold/50 text-gold text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full mb-4" data-id="element-207">
              BONUS #2
            </div>
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4" data-id="element-208">
              <div className="flex-1" data-id="element-209">
                <div className="flex items-center gap-3 mb-2" data-id="element-210">
                  <span className="text-3xl" data-id="element-211">🛡️</span>
                  <h3 className="font-serif text-xl md:text-2xl text-cream" data-id="element-212">
                    The Frequency Shield — Never Fall Back Into The Dark
                  </h3>
                </div>
                <p className="text-cream/50 text-sm mb-4" data-id="element-213">
                  Your Permanent Protection Protocol For After The 7 Days
                </p>
                <div className="text-cream/70 text-base leading-relaxed max-w-lg space-y-3" data-id="element-214">
                  <p data-id="element-215">
                    Clearing is only half the victory. Learn the daily
                    micro-habits to maintain your energetic clarity so
                    interference never creeps back in.
                  </p>
                  <p className="text-cream font-medium" data-id="element-216">
                    You worked hard to clear this. This bonus makes sure it
                    stays clear.
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end flex-shrink-0 md:pt-8" data-id="element-217">
                <span className="price-strike text-2xl line-through" data-id="element-218">$37</span>
                <span className="text-gold font-bold text-3xl mt-2 animate-free-glow" data-id="element-219">
                  FREE
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* PART 3: Total Value Comparison Bar */}
        <div className="reveal-card opacity-0 translate-y-8 transition-all duration-700 ease-out w-full bg-[#1A2E45] border-t border-b border-gold/30 rounded-xl p-8 md:p-10 mb-16 overflow-hidden" data-id="element-220">
          {/* Product Image */}
          <div className="flex justify-center mb-8" data-id="element-221">
            <img src="https://cdn.magicpatterns.com/uploads/7yRUBKYpmMoNyWBeJDtTP3/a1de08b0-4003-4b41-82e1-e7baf9c44421.png" alt="The Lucky Flush — 7-Day Protocol, Quick Start Card & Direct Access to Serena Voss" className="w-full max-w-md rounded-lg shadow-[0_0_40px_rgba(201,168,76,0.15)]" data-id="element-222" />
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-around gap-8 text-center" data-id="element-223">
            <div data-id="element-224">
              <p className="text-cream/50 text-sm uppercase tracking-wider mb-2" data-id="element-225">
                Total Value If Purchased Separately:
              </p>
              <p className="price-strike text-4xl md:text-5xl font-serif inline-block line-through" data-id="element-226">
                $305
              </p>
            </div>
            <div className="hidden md:block w-px h-16 bg-gold/20" data-id="element-227"></div>
            <div data-id="element-228">
              <p className="text-gold text-sm uppercase tracking-[0.15em] font-semibold mb-2" data-id="element-229">
                YOUR INVESTMENT TODAY:
              </p>
              <p className="font-serif text-6xl md:text-7xl text-gold font-bold drop-shadow-[0_0_20px_rgba(201,168,76,0.3)]" data-id="element-230">
                $6.37
              </p>
            </div>
          </div>
          <p className="text-cream/50 text-center text-sm mt-8" data-id="element-231">
            One single payment. Instant delivery. No subscriptions. No recurring
            charges.
          </p>
        </div>

        {/* PART 4: Experience Anchoring Block */}
        <div className="w-3/5 h-px bg-gold/30 mx-auto mb-12" data-id="element-232"></div>

        <p className="text-gold text-sm tracking-[0.2em] uppercase mb-8 font-medium text-center" data-id="element-233">
          WHAT WOMEN TYPICALLY SPEND TO GET THIS SAME RESULT
        </p>

        <div className="w-full max-w-xl mx-auto mb-8" data-id="element-234">
          {alternatives.map((alt, i) => <div key={i} className={`flex justify-between items-center py-4 ${i < alternatives.length - 1 ? 'border-b border-white/5' : ''}`} data-id="element-235">
              <span className="text-cream/80 text-base" data-id="element-236">{alt.label}</span>
              <span className="text-cream/40 text-base font-serif ml-4 flex-shrink-0" data-id="element-237">
                {alt.cost}
              </span>
            </div>)}
        </div>

        <p className="text-gold italic text-center text-lg font-serif max-w-lg mb-16" data-id="element-238">
          "The Lucky Flush delivers the same energetic result — at home, on your
          schedule, for $6.37."
        </p>

        {/* PART 5: Final CTA */}
        <a href="https://pay.hotmart.com/Q105372393V?checkoutMode=10" onClick={() => {
        if (typeof window !== 'undefined' && (window as any).fbq) {
          ;
          (window as any).fbq('track', 'AddToCart');
        }
      }} className="w-full md:w-auto inline-flex items-center justify-center px-10 py-6 bg-cta-red text-white font-bold text-xl md:text-2xl rounded-full animate-heartbeat hover:bg-red-700 transition-colors duration-300 mb-4" data-id="element-239">
          Yes — Clear My Frequency Now → $6.37
        </a>

        <div className="flex items-center justify-center space-x-2 text-gold font-medium text-xs md:text-sm text-center" data-id="element-240">
          <Lock className="w-4 h-4" data-id="element-241" />
          <span data-id="element-242">
            Secure Checkout | Instant Digital Delivery | 7-Day Money Back
            Guarantee
          </span>
        </div>
      </div>
    </section>;
}